// Connections -> Ticketing: the organisation's Jira Cloud, ServiceNow or Azure DevOps (backend
// routes/ticketing.routes.ts, docs/TICKETING.md). integrations:admin connects it; the token is
// tested with the vendor before anything is saved and is never shown again.

import { useCallback, useEffect, useState } from "react";
import { Ticket, Plug, CheckCircle, AlertTriangle } from "lucide-react";
import { theme, card, inputStyle, btnPrimary, btnSecondary, type as text } from "../theme";
import { LoadingBlock } from "../components/Spinner";
import { api, apiErrorMessage } from "../api/client";
import { fmtDateTime } from "../lib/format";

type Provider = "jira" | "servicenow" | "azure_devops";
type Source = "corrective_action" | "vulnerability" | "remediation_step";

interface Status {
  connection: null | {
    provider: Provider; providerLabel: string; baseUrl: string; project: string; itemType: string | null; username: string | null;
    autoPushSources: Source[]; enabled: boolean; connectedAt: string; lastSyncAt: string | null; lastError: string | null; lastErrorAt: string | null;
  };
  tickets: Partial<Record<"open" | "in_progress" | "done" | "cancelled" | "unknown", number>>;
  providers: Array<{ id: Provider; label: string; example: string; defaultItemType: string | null }>;
  sources: Array<{ id: Source; label: string }>;
}

const FIELD_WORDS: Record<Provider, { project: string; projectHint: string; user: string | null; userHint: string; secret: string; secretHint: string; itemType: string | null }> = {
  jira: {
    project: "Project key", projectHint: "The key at the start of each issue number, such as SEC.",
    user: "Account email", userHint: "The Atlassian account the API token belongs to. Use a service account where you can.",
    secret: "API token", secretHint: "Created at id.atlassian.com under Security, API tokens.", itemType: "Issue type",
  },
  servicenow: {
    project: "Table", projectHint: "Where records are created, such as incident or sc_task.",
    user: "User name", userHint: "An integration user with the rest_api_explorer and itil roles, or your equivalent.",
    secret: "Password", secretHint: "The integration user's password.", itemType: null,
  },
  azure_devops: {
    project: "Project", projectHint: "The project name as it appears in Azure DevOps.",
    user: null, userHint: "",
    secret: "Personal access token", secretHint: "Scope Work Items (Read & write) and Project and Team (Read) only.", itemType: "Work item type",
  },
};

const SOURCE_HINTS: Record<Source, string> = {
  corrective_action: "Approved, in progress or blocked actions.",
  vulnerability: "Open P1 and P2 findings without an exception.",
  remediation_step: "Pending steps of a remediation run someone approved.",
};

export function ConnectTicketingPage() {
  const [status, setStatus] = useState<Status | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ provider: "jira" as Provider, baseUrl: "", project: "", itemType: "Task", username: "", secret: "" });

  const load = useCallback(async () => {
    try {
      setStatus(await api.get<Status>("/ticketing/status"));
    } catch (e) {
      setError(apiErrorMessage(e, "Could not load the ticketing connection."));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const run = async (label: string, fn: () => Promise<unknown>, done: string) => {
    setBusy(label); setError(null); setSuccess(null);
    try {
      await fn();
      setSuccess(done);
      await load();
      return true;
    } catch (e) {
      setError(apiErrorMessage(e, "That did not work."));
      return false;
    } finally {
      setBusy(null);
    }
  };

  const openForm = () => {
    const c = status?.connection;
    setForm(c
      ? { provider: c.provider, baseUrl: c.baseUrl, project: c.project, itemType: c.itemType ?? "", username: c.username ?? "", secret: "" }
      : { provider: "jira", baseUrl: "", project: "", itemType: "Task", username: "", secret: "" });
    setShowForm(true);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const words = FIELD_WORDS[form.provider];
    const ok = await run("save", () => api.put("/ticketing/connection", {
      provider: form.provider, baseUrl: form.baseUrl, project: form.project.trim(),
      itemType: words.itemType ? form.itemType.trim() || null : null,
      username: words.user ? form.username.trim() || null : null,
      ...(form.secret ? { secret: form.secret } : {}),
    }), "Connected. The credentials and the project were checked with the ticketing system before saving.");
    if (ok) { setShowForm(false); setForm((f) => ({ ...f, secret: "" })); }
  };

  const toggleSource = (id: Source, on: boolean) => {
    const current = status?.connection?.autoPushSources ?? [];
    const next = on ? [...current, id] : current.filter((s) => s !== id);
    void run("auto", () => api.patch("/ticketing/connection/auto-push", { sources: next }), on ? "Automatic tickets switched on for that source." : "Automatic tickets switched off for that source.");
  };

  if (loading) return <LoadingBlock label="Loading the ticketing connection…" />;
  if (!status) return <div role="alert" style={{ color: "var(--c-dangerText)", padding: 20 }}>{error ?? "Could not load the ticketing connection."}</div>;
  const c = status.connection;
  const words = FIELD_WORDS[form.provider];
  const example = status.providers.find((p) => p.id === form.provider)?.example ?? "";
  const counts = status.tickets;
  const total = Object.values(counts).reduce((n, v) => n + (v ?? 0), 0);

  return (
    <div style={{ maxWidth: 760 }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, marginBottom: 20 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
            <Ticket size={22} color="var(--c-accentText)" aria-hidden />
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: theme.color.heading }}>Ticketing</h2>
            {c && <span style={{ background: "var(--c-successBg)", color: "var(--c-successText)", fontSize: 11, fontWeight: 600, padding: "2px 9px", borderRadius: 2 }}>{c.providerLabel}</span>}
          </div>
          <p style={{ margin: 0, ...text.hint, maxWidth: 560 }}>
            Raise corrective actions, vulnerabilities and remediation steps as tickets in Jira Cloud, ServiceNow or Azure DevOps,
            and see each ticket's status beside the platform's own. A closed ticket marks an action completed; verifying that
            the fix worked is always done here, by a person.
          </p>
        </div>
        {c && !showForm && (
          <div style={{ display: "flex", gap: 8, flexShrink: 0, flexWrap: "wrap", justifyContent: "flex-end" }}>
            <button type="button" onClick={() => run("sync", () => api.post("/ticketing/sync", {}), "Sync started. Statuses update within a few minutes.")} disabled={!!busy} style={{ ...btnPrimary, padding: "7px 14px", fontSize: 13 }}>
              {busy === "sync" ? "Starting…" : "Sync now"}
            </button>
            <button type="button" onClick={() => run("test", () => api.post("/ticketing/connection/test", {}), "The ticketing system accepted the stored credentials.")} disabled={!!busy} style={{ ...btnSecondary, padding: "7px 14px", fontSize: 13 }}>
              {busy === "test" ? "Testing…" : "Test"}
            </button>
            <button type="button" onClick={openForm} disabled={!!busy} style={{ ...btnSecondary, padding: "7px 14px", fontSize: 13 }}>Change</button>
            <button type="button" disabled={!!busy}
              onClick={() => { if (confirm("Disconnect the ticketing system? The stored token is deleted. Tickets already raised keep their links but are no longer read.")) void run("del", () => api.del("/ticketing/connection"), "Disconnected. The stored token was deleted."); }}
              style={{ ...btnSecondary, padding: "7px 14px", fontSize: 13, color: "var(--c-dangerText)" }}>
              Disconnect
            </button>
          </div>
        )}
      </div>

      {error && <div role="alert" style={{ ...card, marginBottom: 16, padding: "10px 14px", background: "var(--c-dangerBg)", border: "1px solid var(--c-danger)", color: "var(--c-dangerText)", fontSize: 13 }}>{error}</div>}
      {success && (
        <div style={{ ...card, marginBottom: 16, padding: "10px 14px", background: "var(--c-successBg)", border: "1px solid var(--c-success)", color: "var(--c-successText)", fontSize: 13, display: "flex", alignItems: "center", gap: 8 }}>
          <CheckCircle size={14} aria-hidden /> {success}
        </div>
      )}

      {!c && !showForm && (
        <div style={{ ...card, textAlign: "center", padding: "40px 28px" }}>
          <div style={{ marginBottom: 12, display: "flex", justifyContent: "center" }}><Plug size={36} color="var(--c-muted)" aria-hidden /></div>
          <div style={{ fontSize: 15, fontWeight: 600, color: theme.color.heading, marginBottom: 8 }}>Connect your ticketing system</div>
          <p style={{ ...text.hint, maxWidth: 540, margin: "0 auto 20px" }}>
            Only the vendor's own cloud address is accepted. Tickets carry the record's reference, title, priority, deadline and a
            link back here; never an owner's or requester's name. Nothing is raised automatically until you switch a source on.
          </p>
          <button type="button" onClick={openForm} style={{ ...btnPrimary, padding: "9px 22px", fontSize: 13 }}>Connect a ticketing system</button>
        </div>
      )}

      {showForm && (
        <form onSubmit={submit} style={{ ...card, display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ ...text.sectionTitle, fontSize: 14, margin: 0 }}>{c ? "Change the connection" : "Connect"}</div>
          <label>
            <span style={text.label}>Ticketing system</span>
            <select value={form.provider} style={inputStyle}
              onChange={(e) => { const p = e.target.value as Provider; setForm((f) => ({ ...f, provider: p, itemType: status.providers.find((x) => x.id === p)?.defaultItemType ?? "" })); }}>
              {status.providers.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
          </label>
          <label>
            <span style={text.label}>Site address</span>
            <input type="url" required value={form.baseUrl} placeholder={example} onChange={(e) => setForm((f) => ({ ...f, baseUrl: e.target.value }))} style={inputStyle} />
            <span style={{ ...text.meta, display: "block", marginTop: 4 }}>HTTPS on the vendor's own cloud host only, for example {example}.</span>
          </label>
          <div style={{ display: "grid", gridTemplateColumns: words.itemType ? "1fr 1fr" : "1fr", gap: 12 }}>
            <label>
              <span style={text.label}>{words.project}</span>
              <input required value={form.project} onChange={(e) => setForm((f) => ({ ...f, project: e.target.value }))} style={inputStyle} />
              <span style={{ ...text.meta, display: "block", marginTop: 4 }}>{words.projectHint}</span>
            </label>
            {words.itemType && (
              <label>
                <span style={text.label}>{words.itemType}</span>
                <input value={form.itemType} onChange={(e) => setForm((f) => ({ ...f, itemType: e.target.value }))} style={inputStyle} />
              </label>
            )}
          </div>
          {words.user && (
            <label>
              <span style={text.label}>{words.user}</span>
              <input required value={form.username} autoComplete="off" onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))} style={inputStyle} />
              <span style={{ ...text.meta, display: "block", marginTop: 4 }}>{words.userHint}</span>
            </label>
          )}
          <label>
            <span style={text.label}>{words.secret}</span>
            <input type="password" autoComplete="new-password" required={!c} value={form.secret} onChange={(e) => setForm((f) => ({ ...f, secret: e.target.value }))} style={inputStyle}
              placeholder={c ? "Leave empty to keep the stored one (same site and user only)" : ""} />
            <span style={{ ...text.meta, display: "block", marginTop: 4 }}>{words.secretHint} Stored encrypted for this organisation and never shown again.</span>
          </label>
          <div style={{ display: "flex", gap: 10 }}>
            <button type="submit" disabled={!!busy} style={{ ...btnPrimary, padding: "9px 20px", fontSize: 13 }}>{busy === "save" ? "Checking and saving…" : "Check and save"}</button>
            <button type="button" onClick={() => setShowForm(false)} style={{ ...btnSecondary, padding: "9px 16px", fontSize: 13 }}>Cancel</button>
          </div>
        </form>
      )}

      {c && !showForm && (
        <>
          <div style={{ ...card, padding: "16px 20px", marginBottom: 16 }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 14, fontSize: 13 }}>
              <Fact label="Site" value={c.baseUrl} />
              <Fact label={FIELD_WORDS[c.provider].project} value={c.project + (c.itemType ? ` (${c.itemType})` : "")} />
              {c.username && <Fact label={FIELD_WORDS[c.provider].user ?? "User"} value={c.username} />}
              <Fact label="Token" value="Stored encrypted" />
              <Fact label="Connected" value={fmtDateTime(c.connectedAt)} />
              <Fact label="Last read" value={c.lastSyncAt ? fmtDateTime(c.lastSyncAt) : "Not yet (hourly)"} />
              <Fact label="Tickets" value={total ? `${total} raised · ${counts.done ?? 0} done · ${(counts.open ?? 0) + (counts.in_progress ?? 0)} open` : "None raised yet"} />
            </div>
          </div>

          {c.lastError && (
            <div role="alert" style={{ ...card, padding: "10px 14px", marginBottom: 16, background: "var(--c-dangerBg)", border: "1px solid var(--c-danger)", color: "var(--c-dangerText)", fontSize: 13, display: "flex", gap: 8 }}>
              <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 2 }} aria-hidden />
              <div><strong>The last sync failed:</strong> {c.lastError}<div style={{ fontSize: 11, marginTop: 4 }}>It is retried every hour, and shows on My Actions until it works.</div></div>
            </div>
          )}

          <div style={{ ...card, padding: "16px 20px", marginBottom: 16 }}>
            <div style={{ ...text.sectionTitle, fontSize: 14 }}>Raise tickets automatically</div>
            <p style={{ ...text.hint, margin: "0 0 12px" }}>Off by default. Each hour, at most 25 new tickets are raised for the sources switched on here. Anyone who can write a register can always raise a ticket by hand from its page.</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {status.sources.map((s) => (
                <label key={s.id} style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer" }}>
                  <input type="checkbox" checked={c.autoPushSources.includes(s.id)} disabled={!!busy} onChange={(e) => toggleSource(s.id, e.target.checked)} style={{ marginTop: 3 }} />
                  <span><span style={text.itemTitle}>{s.label}</span><span style={{ ...text.hint, display: "block" }}>{SOURCE_HINTS[s.id]}</span></span>
                </label>
              ))}
            </div>
          </div>

          <div style={{ ...card, padding: "16px 20px" }}>
            <div style={{ ...text.sectionTitle, fontSize: 14 }}>How a ticket's status is used</div>
            <ul style={{ ...text.hint, margin: 0, paddingLeft: 18 }}>
              <li>Each ticket is read back hourly and shown beside its record, with the vendor's own status name.</li>
              <li>A done ticket moves an in-progress corrective action to completed. It never verifies it, and never completes one whose root cause is still to be recorded.</li>
              <li>A reopened ticket, or a closed ticket whose record is still open, becomes an item on My Actions. Nothing is reopened automatically.</li>
              <li>A vulnerability closes only when the scanner or Defender stops reporting it.</li>
            </ul>
          </div>
        </>
      )}
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div style={{ ...text.meta, marginBottom: 2 }}>{label}</div>
      <div style={{ ...text.body, wordBreak: "break-word" }}>{value}</div>
    </div>
  );
}
